import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const emptyProg = {
  name: "", type: "full_package", description: "", duration_days: 1, currency: "USD",
  flights: [], hotels: [], visas: [], tours: [], itinerary: [], pricing: [],
};

export default function Programs() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProg);

  const load = async () => {
    const { data } = await api.get("/programs");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyProg); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyProg, ...p }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, duration_days: parseInt(form.duration_days || 1) };
      if (editing) await api.put(`/programs/${editing.id}`, payload);
      else await api.post("/programs", payload);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/programs/${id}`);
    load();
  };

  const addRow = (key, blank) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), blank] }));
  const updateRow = (key, i, patch) =>
    setForm((f) => ({ ...f, [key]: f[key].map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const removeRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-6" data-testid="programs-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">{t("programs")}</div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("programs")}</h1>
          <div className="mt-3 divider-accent" />
        </div>
        <Button onClick={openNew} data-testid="program-add-btn" className="btn-accent">
          <Plus size={16} className="me-2" /> {t("newProgram")}
        </Button>
      </div>

      <div className="dashboard-card">
        {items.length === 0 ? (
          <div className="text-center py-12 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("programName")}</TableHead>
                <TableHead>{t("programType")}</TableHead>
                <TableHead>{t("durationDays")}</TableHead>
                <TableHead>{t("currency")}</TableHead>
                <TableHead className="text-end">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{t(p.type === "full_package" ? "fullPackage" : p.type === "flight_hotel" ? "flightHotel" : p.type === "flight_only" ? "flightOnly" : "custom")}</TableCell>
                  <TableCell>{p.duration_days}</TableCell>
                  <TableCell>{p.currency}</TableCell>
                  <TableCell className="text-end space-x-1 rtl:space-x-reverse">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="text-red-600"><Trash2 size={15} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? t("editProgram") : t("addProgram")}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("programName")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="program-name-input" /></Field>
            <Field label={t("programType")}>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="program-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_package">{t("fullPackage")}</SelectItem>
                  <SelectItem value="flight_hotel">{t("flightHotel")}</SelectItem>
                  <SelectItem value="flight_only">{t("flightOnly")}</SelectItem>
                  <SelectItem value="custom">{t("custom")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("durationDays")}><Input type="number" min={1} value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} /></Field>
            <Field label={t("currency")}><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <Field label={t("description")}><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            </div>
          </div>

          <Tabs defaultValue="flights" className="mt-4">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="flights">{t("flights")}</TabsTrigger>
              <TabsTrigger value="hotels">{t("hotels")}</TabsTrigger>
              <TabsTrigger value="visas">{t("visas")}</TabsTrigger>
              <TabsTrigger value="tours">{t("tours")}</TabsTrigger>
              <TabsTrigger value="itinerary">{t("itinerary")}</TabsTrigger>
              <TabsTrigger value="pricing">{t("pricing")}</TabsTrigger>
            </TabsList>

            <TabsContent value="flights" className="space-y-2">
              <RowsHeader onAdd={() => addRow("flights", { airline: "", flight_no: "", departure_airport: "", departure_time: "", arrival_airport: "", arrival_time: "", date: "" })} t={t} />
              {form.flights.map((r, i) => (
                <RowGrid key={i} onRemove={() => removeRow("flights", i)} cols={12}>
                  <Input className="col-span-3" placeholder={t("airline")} value={r.airline} onChange={(e) => updateRow("flights", i, { airline: e.target.value })} />
                  <Input className="col-span-2" placeholder={t("flightNo")} value={r.flight_no || ""} onChange={(e) => updateRow("flights", i, { flight_no: e.target.value })} />
                  <Input className="col-span-2" placeholder={t("departureAirport")} value={r.departure_airport || ""} onChange={(e) => updateRow("flights", i, { departure_airport: e.target.value })} />
                  <Input className="col-span-2" type="datetime-local" value={r.departure_time || ""} onChange={(e) => updateRow("flights", i, { departure_time: e.target.value })} />
                  <Input className="col-span-2" placeholder={t("arrivalAirport")} value={r.arrival_airport || ""} onChange={(e) => updateRow("flights", i, { arrival_airport: e.target.value })} />
                  <Input className="col-span-2" type="datetime-local" value={r.arrival_time || ""} onChange={(e) => updateRow("flights", i, { arrival_time: e.target.value })} />
                </RowGrid>
              ))}
            </TabsContent>

            <TabsContent value="hotels" className="space-y-2">
              <RowsHeader onAdd={() => addRow("hotels", { name: "", city: "", check_in: "", check_in_time: "", check_out: "", check_out_time: "", room_type: "", nights: 1, confirmation_number: "" })} t={t} />
              {form.hotels.map((r, i) => (
                <RowGrid key={i} onRemove={() => removeRow("hotels", i)} cols={12}>
                  <Input className="col-span-3" placeholder={t("name")} value={r.name} onChange={(e) => updateRow("hotels", i, { name: e.target.value })} />
                  <Input className="col-span-2" placeholder={t("city")} value={r.city} onChange={(e) => updateRow("hotels", i, { city: e.target.value })} />
                  <Input className="col-span-2" type="date" title={t("checkIn")} value={r.check_in} onChange={(e) => updateRow("hotels", i, { check_in: e.target.value })} />
                  <Input className="col-span-1" type="time" title={t("checkInTime")} value={r.check_in_time || ""} onChange={(e) => updateRow("hotels", i, { check_in_time: e.target.value })} />
                  <Input className="col-span-2" type="date" title={t("checkOut")} value={r.check_out} onChange={(e) => updateRow("hotels", i, { check_out: e.target.value })} />
                  <Input className="col-span-1" type="time" title={t("checkOutTime")} value={r.check_out_time || ""} onChange={(e) => updateRow("hotels", i, { check_out_time: e.target.value })} />
                  <Input className="col-span-3" placeholder={t("roomType")} value={r.room_type} onChange={(e) => updateRow("hotels", i, { room_type: e.target.value })} />
                  <Input className="col-span-2" type="number" min={0} placeholder={t("nights")} value={r.nights} onChange={(e) => updateRow("hotels", i, { nights: parseInt(e.target.value || "0") })} />
                  <Input className="col-span-6" placeholder={t("confirmationNo")} value={r.confirmation_number || ""} onChange={(e) => updateRow("hotels", i, { confirmation_number: e.target.value })} />
                </RowGrid>
              ))}
            </TabsContent>

            <TabsContent value="visas" className="space-y-2">
              <RowsHeader onAdd={() => addRow("visas", { country: "", visa_type: "", fee: 0 })} t={t} />
              {form.visas.map((r, i) => (
                <RowGrid key={i} onRemove={() => removeRow("visas", i)} cols={3}>
                  <Input placeholder={t("country")} value={r.country} onChange={(e) => updateRow("visas", i, { country: e.target.value })} />
                  <Input placeholder={t("visaType")} value={r.visa_type} onChange={(e) => updateRow("visas", i, { visa_type: e.target.value })} />
                  <Input type="number" placeholder={t("fee")} value={r.fee} onChange={(e) => updateRow("visas", i, { fee: parseFloat(e.target.value || "0") })} />
                </RowGrid>
              ))}
            </TabsContent>

            <TabsContent value="tours" className="space-y-2">
              <RowsHeader onAdd={() => addRow("tours", { name: "", city: "", date: "", duration: "", description: "", price: 0 })} t={t} />
              {form.tours.map((r, i) => (
                <RowGrid key={i} onRemove={() => removeRow("tours", i)} cols={12}>
                  <Input className="col-span-3" placeholder={t("tourName")} value={r.name} onChange={(e) => updateRow("tours", i, { name: e.target.value })} />
                  <Input className="col-span-2" placeholder={t("city")} value={r.city} onChange={(e) => updateRow("tours", i, { city: e.target.value })} />
                  <Input className="col-span-2" type="date" value={r.date} onChange={(e) => updateRow("tours", i, { date: e.target.value })} />
                  <Input className="col-span-2" placeholder={t("tourDuration")} value={r.duration} onChange={(e) => updateRow("tours", i, { duration: e.target.value })} />
                  <Input className="col-span-2" type="number" placeholder={t("amount")} value={r.price} onChange={(e) => updateRow("tours", i, { price: parseFloat(e.target.value || "0") })} />
                  <Input className="col-span-12" placeholder={t("description")} value={r.description} onChange={(e) => updateRow("tours", i, { description: e.target.value })} />
                </RowGrid>
              ))}
            </TabsContent>

            <TabsContent value="itinerary" className="space-y-2">
              <RowsHeader onAdd={() => addRow("itinerary", { day: form.itinerary.length + 1, description: "" })} t={t} />
              {form.itinerary.map((r, i) => (
                <RowGrid key={i} onRemove={() => removeRow("itinerary", i)} cols={12}>
                  <Input className="col-span-2" type="number" min={1} value={r.day} onChange={(e) => updateRow("itinerary", i, { day: parseInt(e.target.value || "1") })} />
                  <Input className="col-span-10" placeholder={t("description")} value={r.description} onChange={(e) => updateRow("itinerary", i, { description: e.target.value })} />
                </RowGrid>
              ))}
            </TabsContent>

            <TabsContent value="pricing" className="space-y-2">
              <RowsHeader onAdd={() => addRow("pricing", { item: "", amount: 0 })} t={t} />
              {form.pricing.map((r, i) => (
                <RowGrid key={i} onRemove={() => removeRow("pricing", i)} cols={2}>
                  <Input placeholder={t("item")} value={r.item} onChange={(e) => updateRow("pricing", i, { item: e.target.value })} />
                  <Input type="number" placeholder={t("amount")} value={r.amount} onChange={(e) => updateRow("pricing", i, { amount: parseFloat(e.target.value || "0") })} />
                </RowGrid>
              ))}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="program-save-btn" className="bg-[#0A192F] hover:bg-[#112240] text-white">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);

const RowsHeader = ({ onAdd, t }) => (
  <div className="flex justify-end">
    <Button size="sm" type="button" variant="outline" onClick={onAdd} data-testid="add-row-btn">
      <Plus size={14} className="me-1" /> {t("addRow")}
    </Button>
  </div>
);

const RowGrid = ({ children, onRemove, cols }) => {
  const grid = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4", 6: "sm:grid-cols-6", 12: "sm:grid-cols-12" }[cols] || "sm:grid-cols-4";
  return (
    <div className={`grid grid-cols-1 ${grid} gap-2 items-center border border-dashed border-[hsl(var(--border))] rounded-md p-2`}>
      {children}
      <Button size="icon" variant="ghost" type="button" onClick={onRemove} className="text-red-600 justify-self-end col-span-1 sm:col-span-full sm:justify-self-end w-fit">
        <X size={15} />
      </Button>
    </div>
  );
};
